import { Alert, Dialog, DialogActions, DialogContent, DialogTitle, Stack, Typography } from '@mui/material';
import axios from 'axios';
import config from 'config';
import PaiButton from 'features/ui/base-components/pai-button';
import PaiLink from 'features/ui/base-components/pai-link';
import PaiMenuItem from 'features/ui/base-components/pai-menu-item';
import PaiSelect from 'features/ui/base-components/pai-select';
import PaiTextArea from 'features/ui/base-components/pai-text-area';
import { useUserData } from 'hooks/use-user-data';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { makeStyles } from 'tss-react/mui';

interface CVGenerationModalProps {
  open: boolean;
  onClose: () => void;
  profileId?: string;
  contactName?: string;
  funnelId?: string;
}

interface LanguageOption {
  value: string;
  label: string;
}

const languageOptions: LanguageOption[] = [
  { value: 'en', label: 'English' },
  { value: 'de', label: 'German' },
  { value: 'fr', label: 'French' },
  { value: 'es', label: 'Spanish' },
  { value: 'it', label: 'Italian' },
  { value: 'pt', label: 'Portuguese' },
  { value: 'nl', label: 'Dutch' },
];

const CVGenerationDialog: React.FC<CVGenerationModalProps> = ({ open, onClose, profileId, contactName, funnelId }) => {
  const { t } = useTranslation();
  const { token } = useUserData();
  const { classes } = useStyles();

  const [language, setLanguage] = useState<string>('en');
  const [additionalInfo, setAdditionalInfo] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  const handleAdditionalInfoChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setAdditionalInfo(event.target.value);
  };

  const handleGenerateCV = async () => {
    if (!profileId) {
      setError(t('noProfileIdError'));
      return;
    }

    setLoading(true);
    setError(null);

    try {
      // Make API call to generate CV
      const response = await axios({
        url: `${config.apiBaseUrl}/ai-builders/generate-profile-cv`,
        method: 'POST',
        data: {
          profileId,
          language,
          additionalInfo,
          funnelId,
        },
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        responseType: 'blob', // Important for handling document download
      });

      // Create a download link for the file
      const fileName = `${contactName || 'Contact'}_CV.docx`;
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', fileName);
      document.body.appendChild(link);
      link.click();
      link.remove();

      // Close the modal
      onClose();
    } catch (err: any) {
      console.error('Error generating CV:', err);

      // Check for specific API error responses
      if (err.response && err.response.data) {
        try {
          // Try to read the blob as text to extract the error message
          const reader = new FileReader();
          reader.onload = () => {
            try {
              const errorData = JSON.parse(reader.result as string);
              if (errorData.message) {
                setError(errorData.message);
              } else {
                setError(t('cvGenerationError'));
              }
            } catch (parseError) {
              console.error('Error while parsing JSON:', parseError);
              setError(t('cvGenerationError'));
            }
          };
          reader.readAsText(err.response.data);
        } catch (readerError) {
          console.error('Error while reading blob:', readerError);
          setError(t('cvGenerationError'));
        }
      } else {
        setError(t('cvGenerationError'));
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog classes={{ paper: classes.paper }} open={open} maxWidth={'sm'} fullWidth>
      <DialogTitle className={classes.title}>
        <Typography className={classes.titleText}>{t('generateCV')}</Typography>
      </DialogTitle>
      <DialogContent className={classes.content}>
        <Stack spacing={3} sx={{ mt: 2 }}>
          {error && <Alert severity="error">{error}</Alert>}

          <PaiSelect value={language} label={t('selectLanguage')} variant={'white'} onChange={(e) => setLanguage(e.target.value as string)}>
            {languageOptions.map((option) => (
              <PaiMenuItem key={option.value} value={option.value}>
                {option.label}
              </PaiMenuItem>
            ))}
          </PaiSelect>

          <PaiTextArea
            value={additionalInfo}
            label={t('additionalInformation')}
            placeholder={t('additionalInfoPlaceholder')}
            variant={'white'}
            onChange={handleAdditionalInfoChange}
          />
        </Stack>
      </DialogContent>
      <DialogActions className={classes.actions}>
        <PaiLink text={t('cancel')} size={'md'} variant={'default'} underline={'always'} disabled={loading} action={onClose} />
        <PaiButton text={loading ? t('generating') : t('generateCV')} size={'small'} border={'rounded'} loading={loading} action={handleGenerateCV} />
      </DialogActions>
    </Dialog>
  );
};

const useStyles = makeStyles()((theme) => ({
  paper: {
    borderRadius: '16px',
  },
  title: {
    background: 'linear-gradient(to right, #5044FF, #4AA4FF)',
  },
  titleText: {
    fontSize: theme.typography.pxToRem(15),
    fontWeight: 'bold',
    textTransform: 'uppercase',
    color: '#FFF',
    textAlign: 'center',
  },
  content: {
    padding: '20px 24px !important',
    whiteSpace: 'pre-wrap',
  },
  text: {
    fontSize: theme.typography.pxToRem(15),
    whiteSpace: 'pre-wrap',
  },
  actions: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-end',
    gap: '16px',
    padding: '0 24px 20px 24px',
  },
}));

export default CVGenerationDialog;
